import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { RefreshCw } from 'lucide-react';

export const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { exchangeSession, loginWithGoogle } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract session_id from URL fragment (check both sources)
        const hash = location.hash || window.location.hash;
        console.log('[AuthCallback] Hash:', hash);
        const match = hash.match(/session_id=([^&]+)/);
        
        if (!match) {
          // Wait briefly and retry — fragment might not be available yet
          await new Promise(r => setTimeout(r, 300));
          const retryHash = window.location.hash;
          console.log('[AuthCallback] Retry hash:', retryHash);
          const retryMatch = retryHash.match(/session_id=([^&]+)/);
          if (!retryMatch) {
            console.error('[AuthCallback] No session_id found after retry');
            setError(true);
            return;
          }
          // Use retry match
          await doExchange(retryMatch[1]);
          return;
        }

        await doExchange(match[1]);
      } catch (err) {
        console.error('[AuthCallback] Unexpected error:', err);
        setError(true);
      }
    };

    const doExchange = async (sessionId) => {
      console.log('[AuthCallback] Exchanging session_id:', sessionId.substring(0, 8) + '...');
      let retries = 2;
      let lastError = null;
      while (retries >= 0) {
        try {
          await exchangeSession(sessionId);
          console.log('[AuthCallback] Exchange successful, navigating to /shop');
          // Clean the URL fragment
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/shop', { replace: true });
          return;
        } catch (err) {
          lastError = err;
          console.warn(`[AuthCallback] Exchange attempt failed (${2 - retries + 1}/3):`, err?.response?.status, err?.message);
          retries--;
          if (retries >= 0) {
            await new Promise(r => setTimeout(r, 800));
          }
        }
      }
      console.error('[AuthCallback] All retries failed:', lastError);
      setError(true);
    };

    processSession();
  }, [location.hash, exchangeSession, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback-error">
        <div className="text-center bg-[#121212] rounded-2xl p-10 border border-white/10 max-w-md mx-4">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
            <RefreshCw className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white font-outfit mb-3">Connexion échouée</h2>
          <p className="text-gray-400 mb-6">La connexion Google n'a pas abouti. Veuillez réessayer.</p>
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => loginWithGoogle()}
              className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white py-5"
              data-testid="retry-google-login"
            >
              Réessayer avec Google
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              className="border-white/20 text-white hover:bg-white/10 py-5"
            >
              Retour à la connexion
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-fuchsia-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-400">Connexion en cours...</p>
      </div>
    </div>
  );
};
